import { DEFAULT_CURRENCY, moneyFormat } from "@redotech/money/util";
import { t } from "@redotech/redo-i18n";
import { PriceOption } from "@redotech/redo-return-core-common/condition-options";
import { Condition } from "@redotech/redo-return-core-common/return-flow/condition";
import { CONDITION_NAMES } from "@redotech/redo-return-core-common/return-flow/condition-names";
import {
  priceConditionFromModel,
  validatePriceCondition,
  type PriceState,
} from "@redotech/redo-return-core-common/return-flow/condition-validate";
import * as React from "react";
import { ConditionType, ConditionTypeDetailsProps } from "../condition";

export const PRICE = (
  Details: React.ComponentType<ConditionTypeDetailsProps<PriceState>>,
): ConditionType<PriceState, Condition.Price> => ({
  name: CONDITION_NAMES[Condition.PRICE],
  description(state) {
    if (!state.priceOption) {
      return t(
        "The pre-discount price of an individual item the customer is returning",
      );
    }
    switch (state.priceOption) {
      case PriceOption.LESS_THAN:
        return t(
          "The pre-discount price of an individual item is less than {{value}}",
          {
            value: moneyFormat({
              amount: +state.amount,
              currency: DEFAULT_CURRENCY,
            }),
          },
        );
      case PriceOption.GREATER_THAN:
        return t(
          "The pre-discount price of an individual item is greater than {{value}}",
          {
            value: moneyFormat({
              amount: +state.amount,
              currency: DEFAULT_CURRENCY,
            }),
          },
        );
      case PriceOption.EQUALS:
        return t(
          "The pre-discount price of an individual item equals {{value}}",
          {
            value: moneyFormat({
              amount: +state.amount,
              currency: DEFAULT_CURRENCY,
            }),
          },
        );
      case PriceOption.CENTS_EQUALS:
        return t(
          "The pre-discount price of an individual item has cents equal to {{value}}",
          {
            value: moneyFormat({
              amount: +state.amount,
              currency: DEFAULT_CURRENCY,
            }),
          },
        );
    }
  },
  Details(props: ConditionTypeDetailsProps<PriceState>) {
    return React.createElement(Details, props);
  },
  empty: { amount: (0).toFixed(2), priceOption: null },
  fromModel(model) {
    return priceConditionFromModel(model);
  },
  toModel(state) {
    return {
      type: Condition.PRICE,
      amount: +state.amount,
      priceOption: state.priceOption,
    };
  },
  valid(state) {
    return validatePriceCondition(state).valid;
  },
});
